/**
 * Lifetime-aware window clipping for backtest symbols (BT coverage hardening,
 * fix/bt-coverage-lifetime).
 *
 * A young (recently listed) or delisted symbol has a real, LEGITIMATE data
 * lifetime that is shorter than an arbitrary requested backtest window — this
 * is a structural fact about the market, not a data-quality problem. This
 * module computes that lifetime from a symbol's own cached candle timestamps
 * and clips the requested [start, end] window to what the symbol can actually
 * support (including satisfying the warmup requirement from the symbol's own
 * history), reporting exactly why a clip happened so it is never silent.
 *
 * Pure functions only — no I/O, no Node-only APIs. Safe to import from
 * anywhere (browser bundle, Node CLI, API routes), same constraint as
 * `series-coverage.ts`.
 */

import { z } from 'zod';

// ============================================================================
// Types
// ============================================================================

/**
 * Why a symbol's effective trading window differs from the originally
 * requested window. Multiple reasons can apply simultaneously (e.g. a symbol
 * that both listed late AND delisted early within the requested range).
 *
 *  - 'listing'      — the symbol's data does not begin until AFTER
 *                      requestedStart (it didn't exist yet).
 *  - 'warmup-shift'  — the symbol's data begins before (or exactly at)
 *                      requestedStart, but not early enough to also satisfy
 *                      the warmup requirement — the effective start is pushed
 *                      forward to give indicators a full warmup from the
 *                      symbol's own data.
 *  - 'delisting'     — the symbol's data ends BEFORE requestedEnd.
 */
export type ClipReason = 'listing' | 'delisting' | 'warmup-shift';

/** A symbol's available data lifetime, derived from its own cached candles. */
export interface SymbolLifetime {
  /** Earliest available candle timestamp (Unix ms). */
  firstTs: number;
  /** Latest available candle timestamp (Unix ms). */
  lastTs: number;
}

/**
 * Per-symbol data-coverage report: the originally requested window, the
 * lifetime-clipped effective window actually used for trading, and WHY it
 * was clipped (empty array = not clipped at all). Attached to backtest
 * results (single-asset and aggregate) so a clipped symbol is impossible to
 * miss — see RULE 11 (loud, not silent).
 *
 * Defined via zod (rather than a plain interface) so it can be embedded
 * directly into `BacktestResultSchema` (src/core/types.ts) and get real
 * runtime validation on DB round-trips / API responses, matching the rest of
 * that schema's style. `types.ts` imports `SymbolDataCoverageSchema` from
 * here rather than redefining it, so there is a single source of truth.
 */
export const SymbolDataCoverageSchema = z.object({
  symbol: z.string(),
  requestedRange: z.object({ start: z.number(), end: z.number() }),
  effectiveRange: z.object({ start: z.number(), end: z.number() }),
  clipReasons: z.array(z.enum(['listing', 'delisting', 'warmup-shift'])),
});

export type SymbolDataCoverage = z.infer<typeof SymbolDataCoverageSchema>;

// ============================================================================
// computeLifetime
// ============================================================================

/**
 * Compute a symbol's data lifetime (first/last available timestamp) from an
 * array of candle timestamps. Order-independent (does not require the input
 * to be pre-sorted) and does not mutate the input array.
 *
 * @param timestamps - Unix-ms candle timestamps for a single symbol
 * @returns {firstTs, lastTs}, or null when the array is empty
 */
export function computeLifetime(timestamps: number[]): SymbolLifetime | null {
  if (timestamps.length === 0) return null;

  let firstTs = timestamps[0];
  let lastTs = timestamps[0];
  for (let i = 1; i < timestamps.length; i++) {
    const t = timestamps[i];
    if (t < firstTs) firstTs = t;
    if (t > lastTs) lastTs = t;
  }

  return { firstTs, lastTs };
}

// ============================================================================
// clipWindowToLifetime
// ============================================================================

export interface ClipWindowToLifetimeParams {
  symbol: string;
  requestedStart: number;
  requestedEnd: number;
  lifetime: SymbolLifetime;
  /** Number of bars of history required before the effective start for indicator warmup. */
  warmupBars: number;
  /** Duration of one bar in ms, for this symbol's timeframe. */
  timeframeMs: number;
}

/**
 * Clip a requested [requestedStart, requestedEnd] window to a symbol's real
 * data lifetime, applying the warmup requirement from the symbol's OWN data.
 *
 *   effectiveStart = max(requestedStart, lifetime.firstTs + warmupBars * timeframeMs)
 *   effectiveEnd   = min(requestedEnd, lifetime.lastTs)
 *
 * Throws a loud, actionable error (naming the symbol, its lifetime, the
 * warmup requirement, and the requested range) when the resulting window
 * cannot fit the warmup requirement plus at least one tradable bar
 * (effectiveEnd - effectiveStart < timeframeMs). This is a HARD FAIL — a
 * symbol must never be silently dropped from a portfolio; the caller must
 * either exclude it explicitly or narrow the requested range.
 *
 * @throws Error when the effective window is empty/degenerate
 */
export function clipWindowToLifetime(params: ClipWindowToLifetimeParams): SymbolDataCoverage {
  const { symbol, requestedStart, requestedEnd, lifetime, warmupBars, timeframeMs } = params;

  const warmupMs = warmupBars * timeframeMs;
  const warmupSatisfiedStart = lifetime.firstTs + warmupMs;

  const clipReasons: ClipReason[] = [];

  let effectiveStart: number;
  if (lifetime.firstTs > requestedStart) {
    // The symbol did not exist yet at requestedStart — root cause is 'listing'
    // even though the warmup requirement also pushes the start further out.
    clipReasons.push('listing');
    effectiveStart = Math.max(requestedStart, warmupSatisfiedStart);
  } else if (warmupSatisfiedStart > requestedStart) {
    // The symbol existed before requestedStart, but not long enough before it
    // to also satisfy the warmup requirement.
    clipReasons.push('warmup-shift');
    effectiveStart = warmupSatisfiedStart;
  } else {
    effectiveStart = requestedStart;
  }

  let effectiveEnd: number;
  if (lifetime.lastTs < requestedEnd) {
    clipReasons.push('delisting');
    effectiveEnd = lifetime.lastTs;
  } else {
    effectiveEnd = requestedEnd;
  }

  if (effectiveEnd - effectiveStart < timeframeMs) {
    const fmt = (ts: number): string => new Date(ts).toISOString();
    throw new Error(
      `Cannot backtest ${symbol}: its available data lifetime [${fmt(lifetime.firstTs)} .. ${fmt(lifetime.lastTs)}] ` +
      `does not fit the required warmup (${warmupBars} bars = ${warmupMs}ms) plus at least one tradable bar ` +
      `within the requested range [${fmt(requestedStart)} .. ${fmt(requestedEnd)}]. ` +
      `Computed effective start ${fmt(effectiveStart)} would leave ${effectiveEnd - effectiveStart}ms ` +
      `(< 1 bar of ${timeframeMs}ms) before the effective end ${fmt(effectiveEnd)}. ` +
      `Exclude this symbol from the request, or narrow/shift the requested range to fit its actual lifetime.`,
    );
  }

  return {
    symbol,
    requestedRange: { start: requestedStart, end: requestedEnd },
    effectiveRange: { start: effectiveStart, end: effectiveEnd },
    clipReasons,
  };
}
